import type { RawMqttMessage } from '@/lib/mqtt/types';
import { cn } from '@/lib/utils';
import type { ReactNode } from 'react';
import type { DetailTab } from '../types';
import type { FieldFilter } from '../utils/payloadQuery';
import { CopyMessageButton } from './CopyMessageButton';
import { HistoryChart } from './HistoryChart';
import { JsonView } from './JsonView';
import { MetaRow } from './MetaRow';

interface Props {
  selected: RawMqttMessage | null;
  messages: RawMqttMessage[];
  tab: DetailTab;
  onTab: (t: DetailTab) => void;
  publishSlot?: ReactNode;
  /** When provided, JSON values become click-to-filter targets. */
  onFilter?: (f: FieldFilter) => void;
}

const TABS: DetailTab[] = ['json', 'raw', 'history'];

export function DetailPane({ selected, messages, tab, onTab, publishSlot, onFilter }: Props) {
  return (
    <div className="flex h-full flex-col overflow-hidden">
      <div className="flex items-center gap-1.5 border-b border-(--color-border) bg-(--color-surface) px-3 py-[5px]">
        <span className="mr-1 font-sans text-[0.692em] font-bold text-(--color-text3)">VALUE</span>
        {TABS.map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => onTab(t)}
            className={cn(
              'cursor-pointer rounded border px-2.5 py-[3px] font-sans text-[0.846em] font-bold capitalize transition-colors',
              tab === t
                ? 'border-(--color-green) bg-(--color-green-bg) text-(--color-green)'
                : 'border-transparent text-(--color-text3) hover:bg-(--color-bg)',
            )}
          >
            {t}
          </button>
        ))}
        <CopyMessageButton selected={selected} />
        <span className="ml-auto font-mono text-[0.615em] text-(--color-text3)">
          {selected
            ? new Date(selected.receivedAt).toISOString().replace('T', ' ').substring(0, 23)
            : '—'}
        </span>
      </div>

      <MetaRow payload={selected?.payload ?? null} />

      {selected ? (
        tab === 'history' ? (
          <HistoryChart messages={messages} />
        ) : (
          <JsonView
            payload={selected.payload}
            raw={selected.raw}
            mode={tab}
            {...(onFilter ? { onFilter } : {})}
          />
        )
      ) : (
        <div className="flex-1 p-3.5 font-mono text-[0.769em] italic text-(--color-text3)">
          No message selected
        </div>
      )}

      {publishSlot}
    </div>
  );
}
